import { Grid } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { SelectedCardRequestPayload } from "services/SignalsService";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import SignalsPageStore from "../../../Store/SignalsPageStore";
import {
    getPersistedObjectFilterConflictsAgainstSource,
    resolvePersistedObjectFiltersAgainstSource,
} from "../../ObjectFilters";
import {
    ISignalsChartDefinition,
    SignalsObjectType,
    SignalsPersistedObjectFilters,
} from "../../../SignalsModels";

type Props = {
    open: boolean;
    onClose: () => void;
    chartDefinition?: ISignalsChartDefinition;
    pinState?: {
        vizOptions?: string | null;
        orderRuleId?: string | null;
        getEffectiveVizOptionsForPersistence?: () => string | undefined;
        getSerializedObjectFiltersForPersistence?: () => string | undefined;
    };
};

type PinActionContext = {
    targetDashboardId: string;
    targetDashboardTitle: string;
    payload: SelectedCardRequestPayload;
    hadObjectFilterConflicts: boolean;
};

const RemovedFiltersToastMessage =
    "Conflicting chart filters were adjusted to match your dashboard settings.";

function parsePersistedObjectFilters(
    rawPayload?: string | null,
): SignalsPersistedObjectFilters | null {
    if (!rawPayload) return null;

    try {
        const parsed = JSON.parse(rawPayload);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }

        const payloadRecord = parsed as {
            objectFilters?: unknown;
            objectFilterDateOverride?: unknown;
            objectFilterDateTypeOverride?: unknown;
            objectFilterCallDurationOverride?: unknown;
            objectFilterMessageCountOverride?: unknown;
        };

        if (
            !payloadRecord.objectFilters ||
            typeof payloadRecord.objectFilters !== "object" ||
            Array.isArray(payloadRecord.objectFilters)
        ) {
            return null;
        }

        return {
            objectFilters: payloadRecord.objectFilters as ApplicationFilters,
            objectFilterDateOverride:
                typeof payloadRecord.objectFilterDateOverride === "boolean"
                    ? payloadRecord.objectFilterDateOverride
                    : false,
            objectFilterDateTypeOverride:
                typeof payloadRecord.objectFilterDateTypeOverride === "boolean"
                    ? payloadRecord.objectFilterDateTypeOverride
                    : false,
            objectFilterCallDurationOverride:
                typeof payloadRecord.objectFilterCallDurationOverride ===
                "boolean"
                    ? payloadRecord.objectFilterCallDurationOverride
                    : undefined,
            objectFilterMessageCountOverride:
                typeof payloadRecord.objectFilterMessageCountOverride ===
                "boolean"
                    ? payloadRecord.objectFilterMessageCountOverride
                    : undefined,
        };
    } catch {
        return null;
    }
}

export const ChartPinToDashboardDialog: React.FC<Props> = observer(
    ({ open, onClose, chartDefinition, pinState }) => {
        const signalsPageStore = useStore(SignalsPageStore);
        const messageStore = useStore(MessageStore);
        const applicationFiltersStore = useStore(ApplicationFiltersStore);
        const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
        const [selectedDashboardId, setSelectedDashboardId] = useState<
            string | undefined
        >();
        const [isPinning, setIsPinning] = useState(false);
        const [pendingPinContext, setPendingPinContext] =
            useState<PinActionContext | null>(null);

        const dashboardOptions = useMemo(
            () =>
                signalsPageStore.pinTargetDashboards.map((dash) => ({
                    label: dash.title,
                    value: dash.id,
                })),
            [signalsPageStore.pinTargetDashboards],
        );

        useEffect(() => {
            if (open) {
                setSelectedDashboardId(dashboardOptions[0]?.value);
            } else {
                setSelectedDashboardId(undefined);
                setPendingPinContext(null);
            }
        }, [open, dashboardOptions]);

        const buildPinActionContext = (
            targetDashboardId: string,
        ): PinActionContext | null => {
            const targetDashboard =
                signalsPageStore.allDashboards.get(targetDashboardId);
            if (!targetDashboard) {
                return null;
            }

            let objectFiltersForPinnedCopy =
                pinState?.getSerializedObjectFiltersForPersistence?.() ??
                chartDefinition?.objectFilters ??
                undefined;
            let hadObjectFilterConflicts = false;
            const vizOptionsForPinnedCopy =
                pinState?.getEffectiveVizOptionsForPersistence?.() ??
                pinState?.vizOptions ??
                chartDefinition?.vizOptions;
            const orderRuleIdForPinnedCopy =
                pinState?.orderRuleId ?? chartDefinition?.orderRuleId;

            const pinnedFilter = applicationFiltersStore.getSavedFilterById(
                targetDashboard.pinnedApplicationFilterId,
            );
            const pinnedFilters = pinnedFilter?.value;

            if (objectFiltersForPinnedCopy && pinnedFilters) {
                const persistedObjectFilters = parsePersistedObjectFilters(
                    objectFiltersForPinnedCopy,
                );
                if (persistedObjectFilters) {
                    const conflicts =
                        getPersistedObjectFilterConflictsAgainstSource(
                            pinnedFilters,
                            persistedObjectFilters,
                            { agents: applicationFilterValuesStore.agents },
                        );
                    if (conflicts.length > 0) {
                        hadObjectFilterConflicts = true;
                        const resolvedFilters =
                            resolvePersistedObjectFiltersAgainstSource(
                                pinnedFilters,
                                persistedObjectFilters,
                                { agents: applicationFilterValuesStore.agents },
                            );
                        objectFiltersForPinnedCopy =
                            JSON.stringify(resolvedFilters);
                    }
                }
            }

            const payloadType =
                chartDefinition?.objectType === SignalsObjectType.Table
                    ? SignalsObjectType.Table
                    : chartDefinition?.objectType === SignalsObjectType.Unique
                    ? SignalsObjectType.Unique
                    : SignalsObjectType.Chart;

            const payload: SelectedCardRequestPayload = {
                type: payloadType,
                info: {
                    dataEndpoint: chartDefinition?.dataEndpoint,
                    title: chartDefinition?.title,
                    subtitle: chartDefinition?.subtitle,
                    xTitle: chartDefinition?.xTitle,
                    yTitle: chartDefinition?.yTitle,
                    vizOptions: vizOptionsForPinnedCopy,
                    typeOfChart: chartDefinition?.typeOfChart,
                    typeOfDataSelectedInChart:
                        chartDefinition?.typeOfDataSelectedInChart,
                    availableRenderModes: chartDefinition?.availableRenderModes,
                    defaultRenderMode: chartDefinition?.defaultRenderMode,
                    isPaginated: chartDefinition?.isPaginated,
                    compareToPreviousModeIsAvailable:
                        chartDefinition?.compareToPreviousModeIsAvailable,
                    isTemporal: chartDefinition?.isTemporal,
                    showDataGrid: chartDefinition?.showDataGrid,
                    showSpotlight: chartDefinition?.showSpotlight,
                    showAnalyzeWithKBo: chartDefinition?.showAnalyzeWithKBo,
                    objectFilters: objectFiltersForPinnedCopy,
                    orderRuleId: orderRuleIdForPinnedCopy,
                },
            };

            return {
                targetDashboardId: targetDashboard.id,
                targetDashboardTitle: targetDashboard.title,
                payload,
                hadObjectFilterConflicts,
            };
        };

        const runPinAction = async (pinContext: PinActionContext) => {
            const targetDashboard = signalsPageStore.allDashboards.get(
                pinContext.targetDashboardId,
            );
            if (!targetDashboard) {
                messageStore.logError(
                    "Unable to pin report: dashboard not found.",
                );
                return;
            }

            try {
                setIsPinning(true);
                const added = await targetDashboard.addCards([
                    pinContext.payload,
                ]);
                if (!added) return;

                if (pinContext.hadObjectFilterConflicts) {
                    messageStore.logMessage(
                        RemovedFiltersToastMessage,
                        "success",
                    );
                } else {
                    messageStore.logMessage(
                        `Report pinned to ${targetDashboard.title}.`,
                        "success",
                    );
                }

                setPendingPinContext(null);
                onClose();
            } catch (error) {
                messageStore.logError(
                    "Failed to pin report to the selected dashboard.",
                );
            } finally {
                setIsPinning(false);
            }
        };

        const handlePin = async () => {
            if (!chartDefinition || !selectedDashboardId) return;

            const pinContext = buildPinActionContext(selectedDashboardId);
            if (!pinContext) {
                messageStore.logError(
                    "Unable to pin report: dashboard not found.",
                );
                return;
            }

            if (pinContext.hadObjectFilterConflicts) {
                setPendingPinContext(pinContext);
                return;
            }

            await runPinAction(pinContext);
        };

        const handleCancelConflictPin = () => {
            if (isPinning) return;
            setPendingPinContext(null);
        };

        const handleConfirmConflictPin = async () => {
            if (!pendingPinContext) return;
            await runPinAction(pendingPinContext);
        };

        return (
            <>
                <AcxDialog
                    title="Pin to Dashboard"
                    subTitle="A copy of this report will be pinned to the selected dashboard and reflect the current selections."
                    dialogContentChildren={
                        <Grid item xs={12}>
                            <AcxSelectSingle
                                key={selectedDashboardId ?? "no-dashboard"}
                                id="chart-pin-dashboard-select"
                                inputLabel="Dashboard (only showing dashboards with fewer than 20 reports)"
                                options={dashboardOptions}
                                valueField="value"
                                labelField="label"
                                defaultValue={dashboardOptions.find(
                                    (opt) => opt.value === selectedDashboardId,
                                )}
                                onChange={(input) =>
                                    setSelectedDashboardId(input?.value)
                                }
                            />
                        </Grid>
                    }
                    isOpen={open}
                    onClose={onClose}
                    hideCancelButton
                    disableEnforceFocus
                >
                    <Grid container item xs={4}>
                        <AcxButton
                            fullWidth
                            color="primary"
                            onClick={handlePin}
                            disabled={
                                !selectedDashboardId ||
                                !chartDefinition ||
                                !!pendingPinContext
                            }
                            loading={isPinning}
                            containerStyle={{
                                width: "215px",
                            }}
                        >
                            Pin
                        </AcxButton>
                    </Grid>
                </AcxDialog>

                <AcxDialog
                    id="pin-chart-object-filter-conflict-dialog"
                    isOpen={!!pendingPinContext}
                    onClose={handleCancelConflictPin}
                    title="Some chart filters will be removed"
                    text={`Some chart filters conflict with dashboard filters on ${
                        pendingPinContext?.targetDashboardTitle ??
                        "this dashboard"
                    }. The conflicting chart filters will be removed. Would you like to proceed?`}
                    maxWidth="sm"
                    contentWidth="420px"
                    hideCloseButton
                    textAlignCenterTitle
                    textAlignCenterContent
                    centerActionButtons
                    disableEnforceFocus
                >
                    <AcxButton
                        id="pin-chart-object-filter-conflict-confirm"
                        color="primary"
                        onClick={handleConfirmConflictPin}
                        loading={isPinning}
                    >
                        Confirm
                    </AcxButton>
                </AcxDialog>
            </>
        );
    },
);
